import React, { useMemo, useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import api from '../../services/api';
import TextInput from '../../components/ui/TextInput';
import { Button } from '../../components/ui/Button';
import { Label } from '../../components/ui/Label';
import { Select } from '../../components/ui/Select';
import { Switch } from '../../components/ui/Switch';
import { Callout } from '../../components/ui/Callout';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { UserIcon, LockIcon } from '../../components/ui/Icons';
import { useApiOptions } from '../../hooks/useApiOptions';

interface ProfileData {
  userEmail: string;
  userFirstName: string;
  userLastName: string;
  userDisplayName?: string;
  userIsActive: boolean;
  typeLocation: 'OFFICE' | 'VESSEL' | 'SUPPLIER' | 'MANNING_AGENT';
  role: 'ADMIN' | 'USER' | 'CLIENT' | 'GUEST';
  departmentId: string;
  vesselId: string;
  manningAgentId: string;
}

interface PasswordData {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

type Section = 'profile' | 'password';

const API_OPTION_ENDPOINTS = {
  departmentId: { endpoint: '/departments', valueField: 'id', labelField: 'name' },
  vesselId: { endpoint: '/vessels', valueField: 'id', labelField: 'vesselName' },
  manningAgentId: { endpoint: '/crew-lookups/manning-agents', valueField: 'id', labelField: 'name' },
};

function initialsFrom(first: string, last: string, email: string): string {
  const a = first.trim().charAt(0);
  const b = last.trim().charAt(0);
  if (a || b) return `${a}${b}`.toUpperCase();
  return (email.trim().charAt(0) || '?').toUpperCase();
}

function passwordStrength(password: string): { score: number; label: string; bar: string } {
  if (!password) return { score: 0, label: '', bar: 'bg-slate-200 dark:bg-slate-700' };
  let score = 0;
  if (password.length >= 6) score += 1;
  if (password.length >= 10) score += 1;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;
  if (score <= 2) return { score, label: 'Weak', bar: 'bg-amber-500' };
  if (score <= 3) return { score, label: 'Fair', bar: 'bg-sky-500' };
  return { score, label: 'Strong', bar: 'bg-emerald-500' };
}

function emptyToNull(value: string): string | null {
  const t = (value || '').trim();
  return t ? t : null;
}

export type ProfileSettingsProps = {
  /** Render without page chrome (used inside Account settings). */
  embedded?: boolean;
  initialSection?: Section;
  hideSectionSwitcher?: boolean;
};

const ProfileSettings: React.FC<ProfileSettingsProps> = ({
  embedded = false,
  initialSection = 'profile',
  hideSectionSwitcher = false,
}) => {
  const { user, refreshUser } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { colors } = useTheme();
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [activeSection, setActiveSection] = useState<Section>(initialSection);
  const apiOptions = useApiOptions(API_OPTION_ENDPOINTS);
  const canEditAccountFields = user?.role === 'ADMIN';

  useEffect(() => {
    setActiveSection(initialSection);
  }, [initialSection]);

  const [profileData, setProfileData] = useState<ProfileData>({
    userEmail: '',
    userFirstName: '',
    userLastName: '',
    userDisplayName: '',
    userIsActive: true,
    typeLocation: 'OFFICE',
    role: 'USER',
    departmentId: '',
    vesselId: '',
    manningAgentId: '',
  });

  const [passwordData, setPasswordData] = useState<PasswordData>({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [profileErrors, setProfileErrors] = useState<Record<string, string>>({});
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (user) {
      setProfileData({
        userEmail: user.userEmail || '',
        userFirstName: user.userFirstName || '',
        userLastName: user.userLastName || '',
        userDisplayName: user.userDisplayName || '',
        userIsActive: user.userIsActive ?? true,
        typeLocation: user.typeLocation || 'OFFICE',
        role: user.role || 'USER',
        departmentId: user.departmentId != null ? String(user.departmentId) : '',
        vesselId: user.vesselId != null ? String(user.vesselId) : '',
        manningAgentId: user.manningAgentId != null ? String(user.manningAgentId) : '',
      });
    }
  }, [user]);

  const displayName = useMemo(() => {
    const custom = profileData.userDisplayName?.trim();
    if (custom) return custom;
    const full = `${profileData.userFirstName} ${profileData.userLastName}`.trim();
    return full || profileData.userEmail || 'Your account';
  }, [profileData]);

  const initials = useMemo(
    () =>
      initialsFrom(profileData.userFirstName, profileData.userLastName, profileData.userEmail),
    [profileData.userFirstName, profileData.userLastName, profileData.userEmail],
  );

  const strength = passwordStrength(passwordData.newPassword);

  const departmentOptions = useMemo(() => {
    const rows = apiOptions.departmentId || [];
    return [
      { value: '', label: 'No department' },
      ...rows.map((row: { id: number; name?: string }) => ({
        value: String(row.id),
        label: row.name || `#${row.id}`,
      })),
    ];
  }, [apiOptions.departmentId]);

  const vesselOptions = useMemo(() => {
    const rows = apiOptions.vesselId || [];
    return [
      { value: '', label: 'Select vessel' },
      ...rows.map((row: { id: number; vesselName?: string }) => ({
        value: String(row.id),
        label: row.vesselName || `#${row.id}`,
      })),
    ];
  }, [apiOptions.vesselId]);

  const manningAgentOptions = useMemo(() => {
    const rows = apiOptions.manningAgentId || [];
    return [
      { value: '', label: 'Select manning agent' },
      ...rows.map((row: { id: number; name?: string }) => ({
        value: String(row.id),
        label: row.name || `#${row.id}`,
      })),
    ];
  }, [apiOptions.manningAgentId]);

  const setProfileField = <K extends keyof ProfileData>(field: K, value: ProfileData[K]) => {
    setProfileData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'typeLocation' && value !== 'VESSEL') {
        next.vesselId = '';
      }
      if (field === 'typeLocation' && value !== 'MANNING_AGENT') {
        next.manningAgentId = '';
      }
      return next;
    });
    if (profileErrors[field]) {
      setProfileErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleProfileChange = (field: keyof ProfileData) => (
    event: { target: { value: string } },
  ) => {
    setProfileField(field, event.target.value as ProfileData[typeof field]);
  };

  const handlePasswordChange = (field: keyof PasswordData) => (
    event: { target: { value: string } },
  ) => {
    setPasswordData({ ...passwordData, [field]: event.target.value });
    if (passwordErrors[field]) {
      setPasswordErrors({ ...passwordErrors, [field]: '' });
    }
  };

  const validateProfile = (): boolean => {
    const errors: Record<string, string> = {};

    if (!profileData.userEmail.trim()) {
      errors.userEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.userEmail)) {
      errors.userEmail = 'Please enter a valid email address';
    }

    if (!profileData.userFirstName.trim()) {
      errors.userFirstName = 'First name is required';
    }

    if (!profileData.userLastName.trim()) {
      errors.userLastName = 'Last name is required';
    }

    if (canEditAccountFields && profileData.typeLocation === 'VESSEL' && !profileData.vesselId) {
      errors.vesselId = 'Select a vessel for vessel-type users';
    }

    if (
      canEditAccountFields &&
      profileData.typeLocation === 'MANNING_AGENT' &&
      !profileData.manningAgentId
    ) {
      errors.manningAgentId = 'Select a manning agent for manning-agent users';
    }

    setProfileErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePassword = (): boolean => {
    const errors: Record<string, string> = {};

    if (!passwordData.oldPassword) {
      errors.oldPassword = 'Current password is required';
    }

    if (!passwordData.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 6) {
      errors.newPassword = 'Password must be at least 6 characters long';
    }

    if (!passwordData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your new password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateProfile()) return;

    setSavingProfile(true);
    try {
      const payload: Record<string, unknown> = {
        userEmail: profileData.userEmail,
        userFirstName: profileData.userFirstName,
        userLastName: profileData.userLastName,
        userDisplayName: profileData.userDisplayName || null,
        departmentId: emptyToNull(profileData.departmentId),
      };

      if (canEditAccountFields) {
        payload.userIsActive = profileData.userIsActive;
        payload.typeLocation = profileData.typeLocation;
        payload.role = profileData.role;
        payload.vesselId =
          profileData.typeLocation === 'VESSEL' ? emptyToNull(profileData.vesselId) : null;
        payload.manningAgentId =
          profileData.typeLocation === 'MANNING_AGENT'
            ? emptyToNull(profileData.manningAgentId)
            : null;
      }

      await api.patch('/profile', payload);
      await refreshUser();
      showSuccess('Profile updated successfully');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Failed to update profile';
      showError(errorMessage);
      if (error?.response?.data?.errors) {
        setProfileErrors(error.response.data.errors);
      }
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePassword()) return;

    setSavingPassword(true);
    try {
      await api.patch('/profile/change-password', {
        oldPassword: passwordData.oldPassword,
        newPassword: passwordData.newPassword,
      });
      showSuccess('Password changed successfully');
      setPasswordData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setPasswordErrors({});
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Failed to change password';
      showError(errorMessage);
      if (error?.response?.data?.errors) {
        setPasswordErrors(error.response.data.errors);
      } else if (errorMessage.includes('Invalid old password') || errorMessage.includes('Current password')) {
        setPasswordErrors({ oldPassword: 'Current password is incorrect' });
      }
    } finally {
      setSavingPassword(false);
    }
  };

  const content = (
    <div className={cn('space-y-5', !embedded && 'mx-auto max-w-3xl p-6')}>
      {!embedded && (
        <div
          className={cn(
            'relative overflow-hidden rounded-2xl border',
            colors.border,
            colors.surface,
          )}
        >
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-br from-[#0a1424] via-[#143052] to-[#3d5a80]"
            aria-hidden
          />
          <div className="relative flex flex-col gap-4 px-5 pb-5 pt-8 sm:flex-row sm:items-end sm:gap-5 sm:px-6 sm:pb-6 sm:pt-10">
            <div
              className={cn(
                'flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border-2 text-xl font-semibold tracking-wide shadow-lg sm:h-[4.5rem] sm:w-[4.5rem] sm:text-2xl',
                'border-white/30 bg-white text-[#0a1424]',
              )}
              aria-hidden
            >
              {initials}
            </div>
            <div className="min-w-0 flex-1 sm:pb-1">
              <p className={cn('text-[11px] font-semibold uppercase tracking-[0.16em]', colors.textMuted)}>
                Account
              </p>
              <h1 className={cn('mt-0.5 truncate text-xl font-semibold tracking-tight sm:text-2xl', colors.text)}>
                {displayName}
              </h1>
              <p className={cn('mt-0.5 truncate text-sm', colors.textSecondary)}>
                {profileData.userEmail || 'No email on file'}
              </p>
            </div>
          </div>
        </div>
      )}

      {!hideSectionSwitcher && (
        <SegmentedControl
          aria-label="Profile sections"
          value={activeSection}
          onChange={setActiveSection}
          options={[
            {
              id: 'profile',
              label: 'Profile',
              icon: <UserIcon className="h-4 w-4" />,
            },
            {
              id: 'password',
              label: 'Security',
              icon: <LockIcon className="h-4 w-4" />,
            },
          ]}
        />
      )}

      {activeSection === 'profile' ? (
        <div className={cn('rounded-2xl border p-5 sm:p-6', colors.border, colors.surface)}>
          <form onSubmit={handleProfileSubmit} className="space-y-6">
            {!embedded && (
              <div className="space-y-1">
                <h2 className={cn('text-base font-semibold', colors.text)}>Personal information</h2>
                <p className={cn('text-sm', colors.textSecondary)}>
                  How you appear across ATLAS and how we reach you.
                </p>
              </div>
            )}

            <div className="space-y-5">
              <div>
                <Label htmlFor="userEmail" required>
                  Email
                </Label>
                <TextInput
                  id="userEmail"
                  name="userEmail"
                  type="email"
                  value={profileData.userEmail}
                  onChange={handleProfileChange('userEmail')}
                  placeholder="Enter email address"
                  required
                  error={profileErrors.userEmail}
                  autoComplete="email"
                  className="mt-1.5"
                />
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <Label htmlFor="userFirstName" required>
                    First Name
                  </Label>
                  <TextInput
                    id="userFirstName"
                    name="userFirstName"
                    type="text"
                    value={profileData.userFirstName}
                    onChange={handleProfileChange('userFirstName')}
                    placeholder="Enter first name"
                    required
                    error={profileErrors.userFirstName}
                    autoComplete="given-name"
                    className="mt-1.5"
                  />
                </div>

                <div>
                  <Label htmlFor="userLastName" required>
                    Last Name
                  </Label>
                  <TextInput
                    id="userLastName"
                    name="userLastName"
                    type="text"
                    value={profileData.userLastName}
                    onChange={handleProfileChange('userLastName')}
                    placeholder="Enter last name"
                    required
                    error={profileErrors.userLastName}
                    autoComplete="family-name"
                    className="mt-1.5"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="userDisplayName">Display Name</Label>
                <TextInput
                  id="userDisplayName"
                  name="userDisplayName"
                  type="text"
                  value={profileData.userDisplayName || ''}
                  onChange={handleProfileChange('userDisplayName')}
                  placeholder="Enter display name (optional)"
                  helperText="Leave blank to use your first and last name"
                  className="mt-1.5"
                />
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                <div>
                  <Label>Active</Label>
                  <div className="mt-2">
                    <Switch
                      checked={profileData.userIsActive}
                      disabled={!canEditAccountFields}
                      onChange={(e) => setProfileField('userIsActive', e.target.checked)}
                      label="Mark as active"
                      labelPosition="left"
                      description={
                        canEditAccountFields
                          ? 'Check if this user is active'
                          : 'Managed by an administrator'
                      }
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="typeLocation" required>
                    Type
                  </Label>
                  <Select
                    id="typeLocation"
                    name="typeLocation"
                    value={profileData.typeLocation}
                    onChange={handleProfileChange('typeLocation')}
                    disabled={!canEditAccountFields}
                    readOnly={!canEditAccountFields}
                    className="mt-1.5"
                    options={[
                      { value: 'OFFICE', label: 'Office' },
                      { value: 'VESSEL', label: 'Vessel' },
                      { value: 'SUPPLIER', label: 'Supplier' },
                      { value: 'MANNING_AGENT', label: 'Manning agent' },
                    ]}
                  />
                </div>

                <div>
                  <Label htmlFor="role" required>
                    Role
                  </Label>
                  <Select
                    id="role"
                    name="role"
                    value={profileData.role}
                    onChange={handleProfileChange('role')}
                    disabled={!canEditAccountFields}
                    readOnly={!canEditAccountFields}
                    className="mt-1.5"
                    options={[
                      { value: 'ADMIN', label: 'Admin' },
                      { value: 'USER', label: 'User' },
                      { value: 'CLIENT', label: 'Client' },
                      { value: 'GUEST', label: 'Guest' },
                    ]}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="departmentId">Department</Label>
                <Select
                  id="departmentId"
                  name="departmentId"
                  value={profileData.departmentId}
                  onChange={handleProfileChange('departmentId')}
                  placeholder="Select department (optional)"
                  helperText="Used to route department-assigned approval steps."
                  className="mt-1.5"
                  options={departmentOptions}
                />
              </div>

              {profileData.typeLocation === 'VESSEL' && (
                <div className={cn('space-y-3 border-t pt-5', colors.border)}>
                  <div className="space-y-1">
                    <h3 className={cn('text-sm font-semibold', colors.text)}>Vessel account</h3>
                    <p className={cn('text-xs', colors.textMuted)}>
                      This vessel is used to filter supply modules for this login.
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="vesselId" required={canEditAccountFields}>
                      Vessel
                    </Label>
                    <Select
                      id="vesselId"
                      name="vesselId"
                      value={profileData.vesselId}
                      onChange={handleProfileChange('vesselId')}
                      disabled={!canEditAccountFields}
                      readOnly={!canEditAccountFields}
                      placeholder="Select vessel"
                      helperText="Required for vessel-type accounts."
                      error={profileErrors.vesselId}
                      className="mt-1.5"
                      options={vesselOptions}
                    />
                  </div>
                </div>
              )}

              {profileData.typeLocation === 'MANNING_AGENT' && (
                <div className={cn('space-y-3 border-t pt-5', colors.border)}>
                  <div className="space-y-1">
                    <h3 className={cn('text-sm font-semibold', colors.text)}>Manning agent account</h3>
                    <p className={cn('text-xs', colors.textMuted)}>
                      This agency scopes the crew pool and crew file for this login.
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="manningAgentId" required={canEditAccountFields}>
                      Manning agent
                    </Label>
                    <Select
                      id="manningAgentId"
                      name="manningAgentId"
                      value={profileData.manningAgentId}
                      onChange={handleProfileChange('manningAgentId')}
                      disabled={!canEditAccountFields}
                      readOnly={!canEditAccountFields}
                      placeholder="Select manning agent"
                      helperText="Required for manning-agent accounts."
                      error={profileErrors.manningAgentId}
                      className="mt-1.5"
                      options={manningAgentOptions}
                    />
                  </div>
                </div>
              )}

              {!canEditAccountFields && (
                <p className={cn('text-xs', colors.textMuted)}>
                  Active status, type, role, vessel, and manning agent are managed by an
                  administrator. Change your password under Security.
                </p>
              )}
            </div>

            <div className={cn('flex justify-end border-t pt-5', colors.border)}>
              <Button
                type="submit"
                variant="default"
                size="default"
                loading={savingProfile}
                disabled={savingProfile}
              >
                Save profile
              </Button>
            </div>
          </form>
        </div>
      ) : (
        <div className={cn('rounded-2xl border p-5 sm:p-6', colors.border, colors.surface)}>
          <form onSubmit={handlePasswordSubmit} className="space-y-6">
            {!embedded && (
              <div className="space-y-1">
                <h2 className={cn('text-base font-semibold', colors.text)}>Change password</h2>
                <p className={cn('text-sm', colors.textSecondary)}>
                  Use a unique password you do not reuse on other systems.
                </p>
              </div>
            )}

            <Callout variant="info" title="Keep your account secure">
              Choose at least 6 characters. Mixing letters, numbers, and symbols makes a stronger
              password.
            </Callout>

            <div className="space-y-5">
              <div>
                <Label htmlFor="oldPassword" required>
                  Current password
                </Label>
                <TextInput
                  id="oldPassword"
                  name="oldPassword"
                  type="password"
                  value={passwordData.oldPassword}
                  onChange={handlePasswordChange('oldPassword')}
                  placeholder="Enter current password"
                  required
                  error={passwordErrors.oldPassword}
                  autoComplete="current-password"
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label htmlFor="newPassword" required>
                  New password
                </Label>
                <TextInput
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange('newPassword')}
                  placeholder="Enter new password"
                  required
                  error={passwordErrors.newPassword}
                  autoComplete="new-password"
                  minLength={6}
                  className="mt-1.5"
                />
                {passwordData.newPassword ? (
                  <div className="mt-2 space-y-1.5">
                    <div className="flex h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                      <div
                        className={cn('h-full rounded-full transition-all duration-300', strength.bar)}
                        style={{ width: `${Math.min(100, (strength.score / 5) * 100)}%` }}
                      />
                    </div>
                    <p className={cn('text-xs', colors.textMuted)}>
                      Strength:{' '}
                      <span className={cn('font-medium', colors.textSecondary)}>{strength.label}</span>
                    </p>
                  </div>
                ) : (
                  <p className={cn('mt-1.5 text-xs', colors.textMuted)}>At least 6 characters</p>
                )}
              </div>

              <div>
                <Label htmlFor="confirmPassword" required>
                  Confirm new password
                </Label>
                <TextInput
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange('confirmPassword')}
                  placeholder="Re-enter new password"
                  required
                  error={passwordErrors.confirmPassword}
                  autoComplete="new-password"
                  minLength={6}
                  className="mt-1.5"
                />
              </div>
            </div>

            <div className={cn('flex justify-end border-t pt-5', colors.border)}>
              <Button
                type="submit"
                variant="default"
                size="default"
                loading={savingPassword}
                disabled={savingPassword}
              >
                Update password
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );

  if (embedded) return content;
  return <div className="h-full overflow-y-auto">{content}</div>;
};

export default ProfileSettings;
